/**
 * A field start a spreadsheet reads as a formula, which could run when the
 * file opens. `toField` prefixes an apostrophe so the field stays text.
 *
 * @type {RegExp}
 */
const FORMULA_START = /^[=+\-@\t\r]/;

/**
 * Quotes every field, not only those RFC 4180 requires. Excel in a `;`
 * locale splits an unquoted field at `;`, and the rest could start with `=`.
 *
 * @param {unknown} value One cell. Null and undefined become empty.
 * @returns {string} The quoted field, with inner quotes doubled.
 */
const toField = value => {
  let text = value == null ? '' : String(value);
  if (FORMULA_START.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

/**
 * @param {unknown[][]} rows The rows, header first, each a list of cells.
 * @returns {string} The rows as CSV text with CRLF line ends, which Excel
 *   reads without a prompt.
 * @example
 * toCsv([['Name', 'Note'], ['Doe, John', 'He said "hi"']]);
 * // '"Name","Note"\r\n"Doe, John","He said ""hi"""\r\n'
 */
export const toCsv = rows =>
  rows.map(row => `${row.map(toField).join(',')}\r\n`).join('');

/**
 * How long the object URL stays valid after the click. Firefox and iOS
 * Safari open the blob on a later task than `click()`, so a URL revoked on
 * the next tick can leave an empty or missing file. FileSaver.js waits this
 * long for the same reason.
 *
 * @type {number}
 */
export const REVOKE_DELAY_MS = 40000;

/**
 * Downloads rows as a CSV file. The BOM tells Excel the file is UTF-8, so
 * accented names survive. The object URL is revoked after `REVOKE_DELAY_MS`.
 *
 * @param {string} fileName The name the browser saves the file as.
 * @param {unknown[][]} rows The rows, header first.
 */
export const downloadCsv = (fileName, rows) => {
  const blob = new Blob(['\uFEFF', toCsv(rows)], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
};
